import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './Messages.css';

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ subject: '', content: '' });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({ total: 0, pending: 0, replied: 0 });

  // 根据当前访问地址自动判断 API 地址
  const API_BASE = process.env.REACT_APP_API_BASE || 
    (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  // 获取留言统计
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/messages/stats/count`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('获取统计失败:', err);
    }
  };

  // 获取留言列表
  const fetchMessages = async (status = null) => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/messages`;
      if (status && status !== 'all') {
        url += `?status=${status}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.items || []);
      }
    } catch (err) {
      console.error('获取留言失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchMessages(activeTab === 'all' ? null : activeTab);
      fetchStats();
    }
  }, [user, activeTab]);

  // 提交留言
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.content.trim()) {
      alert('请填写完整信息');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormData({ subject: '', content: '' });
        setShowForm(false);
        fetchMessages(activeTab === 'all' ? null : activeTab);
        fetchStats();
        alert('留言提交成功！');
      } else {
        const error = await response.json();
        alert(error.detail || '提交失败');
      }
    } catch (err) {
      alert('提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 删除留言
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条留言吗？')) return;

    try {
      const response = await fetch(`${API_BASE}/api/messages/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        fetchMessages(activeTab === 'all' ? null : activeTab);
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.detail || '删除失败');
      }
    } catch (err) {
      alert('删除失败');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: '待回复', class: 'status-pending' },
      replied: { label: '已回复', class: 'status-replied' },
      closed: { label: '已关闭', class: 'status-closed' },
    };
    const s = statusMap[status] || statusMap.pending;
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  return (
    <div className="messages-page">
      <div className="messages-header">
        <div className="header-left">
          <h1>留言板</h1>
          <p>您可以在此提交问题或建议，我们将尽快回复</p>
        </div>
        <button className="new-message-btn" onClick={() => setShowForm(true)}>
          <span>+</span> 新建留言
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="stats-cards">
        <div className={`stat-card ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">全部留言</div>
        </div>
        <div className={`stat-card ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">待回复</div>
        </div>
        <div className={`stat-card ${activeTab === 'replied' ? 'active' : ''}`} onClick={() => setActiveTab('replied')}>
          <div className="stat-value">{stats.replied}</div>
          <div className="stat-label">已回复</div>
        </div>
      </div>

      {/* 留言列表 */}
      <div className="messages-list">
        {loading ? (
          <div className="loading">加载中...</div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <p>暂无留言</p>
            <button onClick={() => setShowForm(true)}>立即留言</button>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className="message-card">
              <div className="message-header">
                <h3>{msg.subject}</h3>
                <div className="message-meta">
                  {getStatusBadge(msg.status)}
                  <span className="message-time">{formatDate(msg.created_at)}</span>
                </div>
              </div>
              <div className="message-content">
                <p>{msg.content}</p>
              </div>
              {msg.reply_content && (
                <div className="message-reply">
                  <div className="reply-header">
                    <span className="reply-icon">💬</span>
                    <span>管理员回复</span>
                    <span className="reply-time">{formatDate(msg.replied_at)}</span>
                  </div>
                  <div className="reply-content">{msg.reply_content}</div>
                </div>
              )}
              <div className="message-actions">
                {msg.status === 'pending' && (
                  <button className="delete-btn" onClick={() => handleDelete(msg.id)}>
                    删除
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* 新建留言弹窗 */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>新建留言</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="message-form">
              <div className="form-group">
                <label>主题</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="请输入留言主题"
                  maxLength={100}
                  required
                />
              </div>
              <div className="form-group">
                <label>内容</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="请输入留言内容"
                  rows={6}
                  maxLength={1000}
                  required
                />
                <div className="char-count">{formData.content.length}/1000</div>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowForm(false)}>
                  取消
                </button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? '提交中...' : '提交'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
